'use client';

import useSWR, { type SWRConfiguration } from 'swr';
import customFetch from '@/lib/utils/fetch';
import type { EventData } from '../detail/market/types';

export interface MarketEventResponse {
  success: boolean;
  data: EventData;
  message?: string;
}

export interface UseMarketEventOptions {
  eventSlug?: string | null;
  eventId?: string | number | null;
  exactMatch?: boolean;
  enabled?: boolean;
  swr?: SWRConfiguration<MarketEventResponse, Error>;
}

type MarketEventKey = readonly [
  'market-event',
  {
    eventSlug: string | null;
    eventId: string | null;
    exactMatch: boolean;
  },
];

export async function fetchMarketEvent({
  eventSlug,
  eventId,
  exactMatch = false,
}: {
  eventSlug?: string | null;
  eventId?: string | number | null;
  exactMatch?: boolean;
}) {
  const trimmedSlug = eventSlug?.trim() || null;
  // slug 位置传进来的纯数字实际是 event id（详情页 URL 上的 ?id= 可能是 id 也可能是 slug），
  // 按 slug 查库匹配不到，这里转走 event_id 分支
  const slugIsEventId = !!trimmedSlug && /^\d+$/.test(trimmedSlug);
  const normalizedSlug = slugIsEventId ? null : trimmedSlug;
  const normalizedId = slugIsEventId
    ? trimmedSlug
    : eventId == null
      ? null
      : String(eventId).trim();

  if (!normalizedSlug && !normalizedId) {
    throw new Error('eventSlug or eventId is required');
  }

  const searchParams = new URLSearchParams();

  if (normalizedSlug) {
    searchParams.set('event_slug', normalizedSlug);
    if (exactMatch) {
      searchParams.set('exact_match', 'true');
    }
  } else if (normalizedId) {
    searchParams.set('event_id', normalizedId);
  }

  const response = await customFetch(`/api/market?${searchParams.toString()}`);

  if (!(response instanceof Response)) {
    throw response instanceof Error ? response : new Error('Failed to fetch market event');
  }

  const result = (await response.json()) as MarketEventResponse;

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch market event');
  }

  return result;
}

export function useMarketEvent(options: UseMarketEventOptions = {}) {
  const eventSlug = options.eventSlug?.trim() || null;
  const eventId = options.eventId == null ? null : String(options.eventId).trim() || null;
  const exactMatch = options.exactMatch ?? false;
  const enabled = options.enabled ?? true;

  const swrKey: MarketEventKey | null =
    enabled && (eventSlug || eventId)
      ? [
          'market-event',
          {
            eventSlug,
            eventId,
            exactMatch,
          },
        ]
      : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<MarketEventResponse, Error>(
    swrKey,
    ([, params]: MarketEventKey) => fetchMarketEvent(params),
    {
      revalidateOnFocus: false,
      keepPreviousData: true,
      ...options.swr,
    },
  );

  return {
    data: data ?? null,
    eventData: data?.data ?? null,
    event: data?.data?.event ?? null,
    markets: data?.data?.markets ?? [],
    tags: data?.data?.tags ?? [],
    totalMarkets: data?.data?.total_markets ?? 0,
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

export default useMarketEvent;
